import { keepDisplayedTimestamp } from "@/lib/utils";
import type {
  ICompasAIChatAction,
  ICompasAIChatHistoryResponse,
  ICompasAIChatMessage,
} from "@/types/compass.types";

const compassSendTimes: Array<{ question: string; iso: string }> = [];

export function rememberCompassSendTime(question: string, iso: string): void {
  compassSendTimes.push({ question, iso });
}

export function forgetCompassSendTime(question: string, iso: string): void {
  for (let i = compassSendTimes.length - 1; i >= 0; i -= 1) {
    const entry = compassSendTimes[i];
    if (entry?.question === question && entry.iso === iso) {
      compassSendTimes.splice(i, 1);
      return;
    }
  }
}

export function resetCompassSendTimesForTests(): void {
  compassSendTimes.length = 0;
}

const isHistoryMessage = (
  item: ICompasAIChatMessage | ICompasAIChatAction
): item is ICompasAIChatMessage => item.type === "message";

/**
 * Prefer the send-time already shown. Match cached/remembered turns
 * positionally so two identical questions (e.g. "yes") keep distinct clocks.
 */
export function mergeCompassHistory(
  cached: ICompasAIChatHistoryResponse | undefined,
  incoming: ICompasAIChatHistoryResponse
): ICompasAIChatHistoryResponse {
  const cachedMessages = (cached?.history ?? []).filter(isHistoryMessage);
  const usedCached = new Set<number>();
  const usedRemembered = new Set<number>();

  const history = incoming.history.map((item) => {
    if (!isHistoryMessage(item)) return item;
    const cachedIndex = cachedMessages.findIndex(
      (turn, index) => !usedCached.has(index) && turn.question === item.question
    );
    if (cachedIndex >= 0) {
      usedCached.add(cachedIndex);
      const cachedTurn = cachedMessages[cachedIndex];
      return {
        ...item,
        creation_date: keepDisplayedTimestamp(cachedTurn?.creation_date, item.creation_date),
      };
    }
    const rememberedIndex = compassSendTimes.findIndex(
      (entry, index) => !usedRemembered.has(index) && entry.question === item.question
    );
    if (rememberedIndex < 0) return item;
    usedRemembered.add(rememberedIndex);
    const remembered = compassSendTimes[rememberedIndex];
    return {
      ...item,
      creation_date: keepDisplayedTimestamp(remembered?.iso, item.creation_date),
    };
  });

  const remaining = compassSendTimes.filter((_, index) => !usedRemembered.has(index));
  compassSendTimes.length = 0;
  compassSendTimes.push(...remaining);

  return { ...incoming, history };
}
